import React, { useEffect, useRef } from 'react';
import BlackCheckMarkIcon from 'app/assets/svgIcons/BlackCheckMarkIcon';
import { useAnchorRelativeOverlayPosition } from '../hooks/useAnchorRelativeOverlayPosition';
import slideOutStyles from '../SlideOutPanel.module.scss';
import pickerStyles from './MilestoneReviewTypeInlinePicker.module.scss';
import { MILESTONE_REVIEW_TYPE } from '../../../../utils/constants';

export type InlineReviewChoice =
  | typeof MILESTONE_REVIEW_TYPE.TBD
  | typeof MILESTONE_REVIEW_TYPE.MILESTONE_NOT_REQUIRED
  | 'Meeting'
  | 'Async';

interface MilestoneReviewTypeInlinePickerProps {
  /** Header label (or cell) used to align the menu — `getBoundingClientRect()` */
  anchorRef: React.MutableRefObject<HTMLElement | null>;
  /** `.milestoneMatrixWrapper` — positioning context (`position: relative`, `overflow: visible`) */
  overlayRootRef: React.RefObject<HTMLElement | null>;
  selectedReviewType?: string;
  /** Review type values that should not appear in the picker (e.g. `['Async']` for MEDIA BRAINSTORM). */
  excludedReviewTypes?: string[];
  onSelect: (choice: InlineReviewChoice) => void;
  onDismiss: () => void;
}

function normalizeReviewType(value: string | undefined): string {
  return (value ?? '').trim();
}

export const MilestoneReviewTypeInlinePicker: React.FC<
  MilestoneReviewTypeInlinePickerProps
> = ({
  anchorRef,
  overlayRootRef,
  selectedReviewType,
  excludedReviewTypes = [],
  onSelect,
  onDismiss
}) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const selected = normalizeReviewType(selectedReviewType);

  useAnchorRelativeOverlayPosition({
    anchorRef,
    overlayRootRef,
    overlayRef: rootRef,
    layoutKey: selected
  });

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onDismiss();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onDismiss]);

  const isTbd = selected === MILESTONE_REVIEW_TYPE.TBD;
  const isNaChoice = selected === MILESTONE_REVIEW_TYPE.MILESTONE_NOT_REQUIRED;
  const isMeeting = selected === 'Meeting';
  const isAsync = selected === 'Async';

  const row = (
    choice: InlineReviewChoice,
    label: string,
    testId: string,
    isSelected: boolean
  ) => (
    <div
      key={testId}
      role="option"
      tabIndex={0}
      aria-selected={isSelected}
      className={slideOutStyles.tierMenuItem}
      data-testid={testId}
      onMouseDown={e => {
        e.preventDefault();
        e.stopPropagation();
      }}
      onClick={() => onSelect(choice)}
    >
      <span>{label}</span>
      {isSelected && (
        <BlackCheckMarkIcon
          width={16}
          height={16}
          className={`${slideOutStyles.checkmark} ${pickerStyles.milestoneReviewCheckmark}`}
        />
      )}
    </div>
  );

  return (
    <div
      ref={rootRef}
      className={pickerStyles.milestoneInlineOverlayMenu}
      role="listbox"
      aria-label="Review type"
      data-testid="milestone-review-type-inline-picker"
    >
      <div className={slideOutStyles.tierDropdownMenu}>
        {row(
          MILESTONE_REVIEW_TYPE.TBD,
          'TBD',
          'milestone-inline-pick-tbd',
          isTbd
        )}
        {row(
          MILESTONE_REVIEW_TYPE.MILESTONE_NOT_REQUIRED,
          'N/A',
          'milestone-inline-pick-na',
          isNaChoice
        )}
        {row('Meeting', 'Meeting', 'milestone-inline-pick-meeting', isMeeting)}
        {!excludedReviewTypes.includes('Async') &&
          row('Async', 'Async', 'milestone-inline-pick-async', isAsync)}
      </div>
    </div>
  );
};
